import { Controller } from "@hotwired/stimulus";
const pdfjs = require("pdfjs-dist/build/pdf.js");
const TextLayerBuilder = require("pdfjs-dist/web/pdf_viewer").TextLayerBuilder;
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

export default class extends Controller {
  static targets = ["canvas", "pdfElement"];
  static values = {
    zoom: { type: Number, default: 50 },
    zoomIncrement: { type: Number, default: 15 }
  };

  connect() {
    this.load();
  }

  // Loads and renders a PDF document from the configured URL
  load() {
    const pdfUrl = this.data.get("url");

    if (pdfUrl === "") {
      return;
    }

    pdfjs
      .getDocument(pdfUrl)
      .promise.then((pdf) => {
        this._renderAllPages(pdf);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  // Orchestrates rendering of all pages in the PDF document
  _renderAllPages(pdf) {
    const pdfElement = this.pdfElementTarget;
    let pageNum = 0;

    while (pageNum < pdf.numPages) {
      pageNum++;
      pdf.getPage(pageNum).then((page) => {
        this._renderSinglePage(page, pdfElement);
      });
    }
  }

  // Renders an individual PDF page with text layer and annotations
  _renderSinglePage(page, pdfElement) {
    const scale = this._calculateResponsiveScale(page);
    const viewport = this._createViewport(page, scale);

    this._createPageElements(page, pdfElement, viewport);
    this._renderPageContent(page, viewport);
  }

  // Calculates responsive scale factor based on container width
  _calculateResponsiveScale(page) {
    const offsetWidth = this.element.offsetWidth;
    const scale = offsetWidth / page.getViewport({ scale: 1.0 }).width;
    const maxScale = 2.0; // Maximum scale factor to prevent oversized rendering

    return Math.min(scale, maxScale);
  }

  // Creates PDF viewport with appropriate scaling
  _createViewport(page, responsiveScale) {
    // we will default to a scale of 2 for any element that is
    // hidden on initial rendering into the DOM e.g., the new apps review workflow
    return page.getViewport({
      scale: responsiveScale * 2 || 2
    });
  }

  // Creates DOM structure for PDF page (canvas, text layer, container)
  _createPageElements(page, pdfElement, viewport) {
    const content = `<div class="page pdf-page-wrapper-${page.pageIndex}">
      <div class="pdf-container">
        <canvas class="pdf-page pdf-page-${page.pageIndex}"></canvas>
        <div class="textLayer"></div>
      </div>
    </div>`;
    pdfElement.insertAdjacentHTML("beforeend", content);

    this._setupPageElementDimensions(page, viewport);
  }

  // Coordinates dimension setup for all page elements
  _setupPageElementDimensions(page, viewport) {
    // We need to use querySelector here because of the dynamic nature of these elements
    // there are n+1 pages in the pdf, so we need to use the page index to get the correct elements
    const canvas = this.element.querySelector(`.pdf-page-${page.pageIndex}`);
    const wrapper = this.element.querySelector(
      `.pdf-page-wrapper-${page.pageIndex}`
    );
    const container = wrapper.querySelector(".pdf-container");
    const textLayerDiv = container.querySelector(".textLayer");

    this._setupCanvasDimensions(canvas, viewport);
    this._setupContainerDimensions(container, viewport);
    this._setupWrapperDimensions(wrapper, viewport);
    this._setupTextLayerDimensions(textLayerDiv, viewport);
  }

  // Sets canvas dimensions to match viewport
  _setupCanvasDimensions(canvas, viewport) {
    // Set canvas dimensions to match viewport exactly
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
  }

  // Sets container dimensions and initial transform scale
  _setupContainerDimensions(container, viewport) {
    // Set container dimensions to match viewport
    container.style.width = `${viewport.width}px`;
    container.style.height = `${viewport.height}px`;
    container.style.transform = "scale(0.5)"; // Keep initial scale at 0.5
  }

  // Sets wrapper dimensions to match scaled container
  _setupWrapperDimensions(wrapper, viewport) {
    // Set wrapper dimensions to match scaled container
    wrapper.style.width = `${viewport.width * 0.5}px`; // Match initial scale
    wrapper.style.height = `${viewport.height * 0.5}px`; // Match initial scale
  }

  // Sets text layer dimensions and positioning
  _setupTextLayerDimensions(textLayerDiv, viewport) {
    // Set text layer dimensions to match viewport exactly
    textLayerDiv.style.width = `${viewport.width}px`;
    textLayerDiv.style.height = `${viewport.height}px`;
  }

  // Renders canvas content, text layer, and annotations for a page
  _renderPageContent(page, viewport) {
    const canvas = this.element.querySelector(`.pdf-page-${page.pageIndex}`);
    const wrapper = this.element.querySelector(
      `.pdf-page-wrapper-${page.pageIndex}`
    );
    const container = wrapper.querySelector(".pdf-container");
    const textLayerDiv = container.querySelector(".textLayer");

    // Render canvas
    page
      .render({
        canvasContext: canvas.getContext("2d"),
        viewport: viewport
      })
      .promise.then(() => {
        return Promise.all([page.getTextContent(), page.getAnnotations()]);
      })
      .then(([textContent, annotations]) => {
        this._renderTextLayer(page, textLayerDiv, viewport, textContent);
        this._renderAnnotations(annotations, textLayerDiv, viewport);
      });
  }

  // Renders the selectable text layer over the PDF page
  _renderTextLayer(page, textLayerDiv, viewport, textContent) {
    const textLayer = new TextLayerBuilder({
      textLayerDiv: textLayerDiv,
      pageIndex: page.pageIndex,
      viewport: viewport
    });
    textLayer.setTextContent(textContent);
    textLayer.render();
  }

  // Renders clickable annotations (links) on the PDF page
  _renderAnnotations(annotations, textLayerDiv, viewport) {
    // Add links after text layer is rendered
    annotations.forEach((annotation) => {
      if (annotation.subtype === "Link") {
        this._createAnnotationLink(annotation, textLayerDiv, viewport);
      }
    });
  }

  // Creates a clickable link element for PDF annotations
  _createAnnotationLink(annotation, textLayerDiv, viewport) {
    const link = document.createElement("a");
    link.href = annotation.url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";

    this._positionAnnotationLink(link, annotation, viewport);
    textLayerDiv.appendChild(link);
  }

  // Positions and styles annotation links to match PDF coordinates
  _positionAnnotationLink(link, annotation, viewport) {
    // Apply the same scaling and positioning as the text layer
    const rect = viewport.convertToViewportRectangle(annotation.rect);

    // Adjust positioning to match text layer
    // rect[0] is the left position
    // rect[1] is the top position
    // rect[2] is the right position
    // rect[3] is the bottom position
    link.style.left = `${Math.floor(rect[0])}px`;
    link.style.top = `${Math.floor(rect[1])}px`;
    link.style.width = `${Math.ceil(rect[2] - rect[0])}px`;

    // Ensure minimum height and use absolute value to handle negative heights
    const height = Math.abs(Math.ceil(rect[3] - rect[1]));
    link.style.height = `${Math.max(height, 20)}px`;
  }

  // Increases zoom level and updates all page containers
  zoomIn(e) {
    e.preventDefault();
    let newZoomValue = this.zoomValue + this.zoomIncrementValue;
    if (newZoomValue <= 200) {
      const containers = this.element.querySelectorAll(".pdf-container");
      const wrappers = this.element.querySelectorAll(".page");

      containers.forEach((container, index) => {
        const wrapper = wrappers[index];
        const scale = newZoomValue / 100;

        // Update container transform
        container.style.transform = `scale(${scale})`;

        // Update wrapper dimensions to match scaled container
        const originalWidth = container.offsetWidth;
        const originalHeight = container.offsetHeight;
        wrapper.style.width = `${originalWidth * scale}px`;
        wrapper.style.height = `${originalHeight * scale}px`;
      });

      this.zoomValue = newZoomValue;
    }
  }

  zoomOut(e) {
    e.preventDefault();
    let newZoomValue = this.zoomValue - this.zoomIncrementValue;

    if (newZoomValue >= 50) {
      const containers = this.element.querySelectorAll(".pdf-container");
      const wrappers = this.element.querySelectorAll(".page");

      containers.forEach((container, index) => {
        const wrapper = wrappers[index];
        const scale = newZoomValue / 100;

        // Update container transform
        container.style.transform = `scale(${scale})`;

        // Update wrapper dimensions to match scaled container
        const originalWidth = container.offsetWidth;
        const originalHeight = container.offsetHeight;
        wrapper.style.width = `${originalWidth * scale}px`;
        wrapper.style.height = `${originalHeight * scale}px`;

        // Ensure wrapper doesn't overflow container
        wrapper.style.maxWidth = "100%";
        wrapper.style.margin = "0 auto";
      });

      this.zoomValue = newZoomValue;
    }
  }
}
